import './main.scss';
import { monitorAuthState } from './modules/firebase/Auth';
import { isMainPage } from './modules/firebase/authUI';
import { initVueNav } from './components/nav/index.vue';
import { initSettingsPage } from './components/settings';
import { initServicePerformanceModal, initAdvancedSearchFilters, initEditWaypointModal, initShipmentActivityPane, initShipmentCustomCreateModal, initShipmentFilesPane } from './components/shipments';
import { UserStore } from './modules/users/UserStore';
import { messageClient } from './modules/common/messaging/MessageClient';
import { initModalAdapter } from './modules/ui/modal';
import { initTooltipAdapter } from './modules/ui/tooltips';
import { initTheme, syncThemePreferenceForUser } from './modules/ui/theme';
import { enhanceDataTablesLengthSelects } from './modules/shipments/utils/enhance-data-tables-length-select';
import { installFirebaseBridge } from './modules/firebase/bridge';
import { initUpdateAvailable } from './components/ui/update-available';
import { versionManager } from './modules/updates/versionManager';
import { initNcrModal } from './components/ncr';
import { initNotificationsPage } from './components/notifications';
import { initializeNotificationPush } from './modules/notifications/PushNotifications';
import { markSubjectNotificationsRead } from './modules/notifications/NotificationClient';

type IdleWindow = Window & {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
  enhanceDataTablesLengthSelects?: typeof enhanceDataTablesLengthSelects;
};

/**
 * Fix iOS Safari viewport height bug
 * Sets CSS custom property --vh to actual viewport height
 * Handles dynamic address bar appearance/disappearance
 */
function setViewportHeight() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

// Set on load and resize (handles Safari address bar changes)
setViewportHeight();
window.addEventListener('resize', setViewportHeight);
// Also update on orientation change for mobile devices
window.addEventListener('orientationchange', () => {
  // Wait for orientation change to complete
  setTimeout(setViewportHeight, 100);
});

initTheme();
(window as IdleWindow).enhanceDataTablesLengthSelects = enhanceDataTablesLengthSelects;
const authReady = monitorAuthState();
installFirebaseBridge(authReady);
window.JidokaMessaging = messageClient;
window.JidokaNotifications = { markSubjectRead: markSubjectNotificationsRead };
initModalAdapter();
initTooltipAdapter();

function scheduleNonCriticalInit(callback: () => void) {
  const idleWindow = window as IdleWindow;

  const run = () => {
    try {
      callback();
    } catch (error) {
      console.error('[App] Deferred UI initialization failed:', error);
    }
  };

  if (typeof idleWindow.requestIdleCallback === 'function') {
    idleWindow.requestIdleCallback(() => run(), { timeout: 500 });
    return;
  }

  window.setTimeout(run, 0);
}

function initAuthenticatedPageUi() {
  initVueNav();
  initNcrModal();

  window.requestAnimationFrame(() => {
    scheduleNonCriticalInit(initServicePerformanceModal);
    scheduleNonCriticalInit(initShipmentCustomCreateModal);
    scheduleNonCriticalInit(initEditWaypointModal);
    scheduleNonCriticalInit(initAdvancedSearchFilters);
    scheduleNonCriticalInit(initShipmentActivityPane);
    scheduleNonCriticalInit(initShipmentFilesPane);
    scheduleNonCriticalInit(initSettingsPage);
    scheduleNonCriticalInit(initNotificationsPage);
  });
}

if (!isMainPage) {
  const initializeAuthenticatedApp = async () => {
    const result = await authReady;
    if (result.state !== 'authenticated') return;
    if (document.readyState === 'loading') {
      await new Promise<void>(resolve => document.addEventListener('DOMContentLoaded', () => resolve(), { once: true }));
    }
    initUpdateAvailable();
    versionManager.start();
    initAuthenticatedPageUi();
    syncThemePreferenceForUser(result.user.uid).catch(error => {
        console.warn('[App] Theme preference sync failed, using local preference:', error);
    });
    UserStore.init().catch(error => {
      console.warn('[App] UserStore initialization failed, proceeding with fallbacks:', error);
    });
    initializeNotificationPush().catch(error => {
      console.warn('[App] Notification push initialization failed:', error);
    });
  };
  void initializeAuthenticatedApp();
}
